/**
 * useBulkCreateOperation - Hook for managing bulk create operations with toast notifications
 *
 * Features:
 * - Start bulk create operations
 * - Show persistent toast with progress
 * - Persist active operations to sessionStorage
 * - Auto-cleanup completed operations
 */

import * as React from 'react';
import { toast } from 'sonner';

import { BulkCreateOperationToast } from '@/components/external-task-bulk-create';
import {
  enhancedApi as externalTaskApi,
  type ExternalTaskBulkCreateOperationSchema,
  type StartExternalTaskBulkCreateRequest,
} from '@/services/api/generated/externalTaskService';

const STORAGE_KEY = 'bulk-create-active-operations';
const CLEANUP_DELAY_MS = 10000; // 10 seconds after completion

interface UseBulkCreateOperationReturn {
  /** Start a new bulk create operation */
  startOperation: (
    request: StartExternalTaskBulkCreateRequest,
  ) => Promise<string>;
  /** Currently active operation IDs */
  activeOperationIds: string[];
  /** Show toast for an existing operation */
  showToastForOperation: (operationId: string) => void;
  /** Dismiss an operation toast */
  dismissOperation: (operationId: string) => void;
  /** Whether an operation is currently starting */
  isStarting: boolean;
}

export const useBulkCreateOperation = (): UseBulkCreateOperationReturn => {
  // Track active operation IDs
  const [activeOperationIds, setActiveOperationIds] = React.useState<string[]>(
    () => {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    },
  );

  // Track toast IDs for each operation
  const toastIds = React.useRef<Map<string, string | number>>(new Map());

  // Start mutation
  const [startBulkCreate, { isLoading: isStarting }] =
    externalTaskApi.useExternalTaskBulkCreateStartMutation();

  // Persist active operations to sessionStorage
  React.useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(activeOperationIds));
    } catch {
      // Ignore storage errors
    }
  }, [activeOperationIds]);

  // Restore toasts for active operations on mount
  React.useEffect(() => {
    activeOperationIds.forEach((operationId) => {
      if (!toastIds.current.has(operationId)) {
        showToastForOperation(operationId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle operation completion
  const handleOperationComplete = React.useCallback(
    (operation: ExternalTaskBulkCreateOperationSchema) => {
      // Schedule cleanup - the toast component already shows the final status
      setTimeout(() => {
        setActiveOperationIds((prev) =>
          prev.filter((id) => id !== operation.id),
        );
        const toastId = toastIds.current.get(operation.id);
        if (toastId) {
          toast.dismiss(toastId);
          toastIds.current.delete(operation.id);
        }
      }, CLEANUP_DELAY_MS);
    },
    [],
  );

  // Dismiss an operation toast
  const dismissOperation = React.useCallback((operationId: string) => {
    const toastId = toastIds.current.get(operationId);
    if (toastId) {
      toast.dismiss(toastId);
      toastIds.current.delete(operationId);
    }
    setActiveOperationIds((prev) => prev.filter((id) => id !== operationId));
  }, []);

  // Show toast for an operation
  const showToastForOperation = React.useCallback(
    (operationId: string) => {
      // Don't show duplicate toasts
      if (toastIds.current.has(operationId)) {
        return;
      }

      const toastId = toast.custom(
        (t) => (
          <BulkCreateOperationToast
            operationId={operationId}
            onComplete={handleOperationComplete}
            onDismiss={() => {
              dismissOperation(operationId);
              toast.dismiss(t);
            }}
          />
        ),
        {
          duration: Infinity, // Don't auto-dismiss
          position: 'bottom-right',
          unstyled: true, // Disable Sonner's default hover animations
        },
      );

      toastIds.current.set(operationId, toastId);
    },
    [handleOperationComplete, dismissOperation],
  );

  // Start a new operation
  const startOperation = React.useCallback(
    async (request: StartExternalTaskBulkCreateRequest): Promise<string> => {
      const result = await startBulkCreate({
        startExternalTaskBulkCreateRequest: request,
      }).unwrap();

      // Add to active operations
      setActiveOperationIds((prev) => [...prev, result.id]);

      // Show toast
      showToastForOperation(result.id);

      return result.id;
    },
    [startBulkCreate, showToastForOperation],
  );

  return {
    startOperation,
    activeOperationIds,
    showToastForOperation,
    dismissOperation,
    isStarting,
  };
};
